import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Searchbar,
  Segmented,
  SegmentedButton,
  Sheet,
  Button,
  Toolbar,
} from 'konsta/react'
import {
  ArrowDownLeft,
  ArrowUpRight,
  Gamepad2,
  Trophy,
  Hash,
  Clock,
  User,
  CreditCard,
  CheckCircle2,
  CircleDashed,
  X,
  Copy,
  TrendingUp,
  FileText,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { formatCurrency, formatRelativeTime, cn } from '@/lib/utils'

// --- Types ---

type TransactionType = 'deposit' | 'withdraw' | 'game' | 'jackpot'

interface Transaction {
  id: string
  type: TransactionType
  user: string
  amount: number
  date: Date
  status: 'completed' | 'pending'
}

// --- Mock Data ---

const mockTransactions: Transaction[] = [
  { id: 'TX001', type: 'deposit', user: '王大明', amount: 10000, date: new Date(Date.now() - 1000 * 60 * 30), status: 'completed' },
  { id: 'TX002', type: 'game', user: 'David Chen', amount: -4520, date: new Date(Date.now() - 1000 * 60 * 45), status: 'completed' },
  { id: 'TX003', type: 'game', user: 'Sarah Lin', amount: 8750, date: new Date(Date.now() - 1000 * 60 * 60), status: 'completed' },
  { id: 'TX004', type: 'withdraw', user: 'Mike Wang', amount: -5000, date: new Date(Date.now() - 1000 * 60 * 60 * 2), status: 'pending' },
  { id: 'TX005', type: 'deposit', user: '小美', amount: 25000, date: new Date(Date.now() - 1000 * 60 * 60 * 4), status: 'completed' },
  { id: 'TX006', type: 'jackpot', user: '王大明', amount: 17777, date: new Date(Date.now() - 1000 * 60 * 60 * 8), status: 'completed' },
  { id: 'TX007', type: 'game', user: 'Alex Kim', amount: -2000, date: new Date(Date.now() - 1000 * 60 * 60 * 12), status: 'completed' },
  { id: 'TX008', type: 'withdraw', user: 'Lucy Wu', amount: -20000, date: new Date(Date.now() - 1000 * 60 * 60 * 24), status: 'completed' },
  { id: 'TX009', type: 'deposit', user: '張三', amount: 8000, date: new Date(Date.now() - 1000 * 60 * 60 * 36), status: 'completed' },
  { id: 'TX010', type: 'game', user: 'David Chen', amount: 3200, date: new Date(Date.now() - 1000 * 60 * 60 * 48), status: 'completed' },
]

// --- Chart Data (7-day volume) ---

const chartData = [
  { day: '週一', volume: 85000 },
  { day: '週二', volume: 120000 },
  { day: '週三', volume: 95000 },
  { day: '週四', volume: 145000 },
  { day: '週五', volume: 180000 },
  { day: '週六', volume: 220000 },
  { day: '週日', volume: 175000 },
]

// --- Tab definitions ---

type TabKey = 'all' | 'deposit' | 'withdraw' | 'game' | 'jackpot'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'deposit', label: '儲值' },
  { key: 'withdraw', label: '提款' },
  { key: 'game', label: '遊戲' },
  { key: 'jackpot', label: '獎金' },
]

// --- Helpers ---

function getTypeConfig(type: TransactionType) {
  switch (type) {
    case 'deposit':
      return { label: '儲值', Icon: ArrowDownLeft, color: '#34C759', bg: 'bg-[#34C759]/15' }
    case 'withdraw':
      return { label: '提款', Icon: ArrowUpRight, color: '#FF3B30', bg: 'bg-[#FF3B30]/15' }
    case 'game':
      return { label: '遊戲', Icon: Gamepad2, color: '#AF52DE', bg: 'bg-[#AF52DE]/15' }
    case 'jackpot':
      return { label: '獎金', Icon: Trophy, color: '#FF9500', bg: 'bg-[#FF9500]/15' }
  }
}

function TransactionMediaIcon({ type }: { type: TransactionType }) {
  const config = getTypeConfig(type)
  const { Icon } = config
  return (
    <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', config.bg)}>
      <Icon className="w-5 h-5" style={{ color: config.color }} />
    </div>
  )
}

function formatAmount(cents: number): string {
  const abs = Math.abs(cents)
  const formatted = formatCurrency(abs)
  return cents >= 0 ? `+${formatted}` : `-${formatted}`
}

function getAmountColor(cents: number): string {
  if (cents > 0) return '#34C759'
  if (cents < 0) return '#FF3B30'
  return '#8E8E93'
}

function DetailRow({
  icon: Icon,
  label,
  value,
  iconColor = '#8E8E93',
  valueColor,
}: {
  icon: React.ElementType
  label: string
  value: string
  iconColor?: string
  valueColor?: string
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2.5 text-[#8E8E93]">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <span className="text-[14px]">{label}</span>
      </div>
      <span
        className="text-[14px] font-medium"
        style={{ color: valueColor || '#000' }}
      >
        {value}
      </span>
    </div>
  )
}

// --- Custom Tooltip ---

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white rounded-lg shadow-lg px-3 py-2 border border-[#E5E5EA]">
      <p className="text-[12px] text-[#8E8E93]">{label}</p>
      <p className="text-[14px] font-semibold text-[#007AFF]">
        {formatCurrency(payload[0].value)}
      </p>
    </div>
  )
}

// --- Component ---

export function AdminTransactionsScreen() {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    let txs = mockTransactions

    // Filter by tab
    if (activeTab !== 'all') {
      txs = txs.filter((tx) => tx.type === activeTab)
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      txs = txs.filter(
        (tx) =>
          tx.user.toLowerCase().includes(q) ||
          tx.id.toLowerCase().includes(q)
      )
    }

    return txs
  }, [activeTab, searchQuery])

  const handleTxTap = (tx: Transaction) => {
    setSelectedTx(tx)
    setSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
    setTimeout(() => setSelectedTx(null), 300)
  }

  // Summary stats
  const stats = useMemo(() => {
    const totalDeposits = mockTransactions
      .filter((t) => t.type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0)
    const totalWithdrawals = mockTransactions
      .filter((t) => t.type === 'withdraw')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0)
    const totalVolume = mockTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0)
    return { totalDeposits, totalWithdrawals, totalVolume }
  }, [])

  return (
    <>
      <Navbar title="交易紀錄" />

      {/* Summary cards */}
      <Block className="!px-4 !mt-2 !mb-0">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center mx-auto mb-1">
              <ArrowDownLeft className="w-4 h-4 text-[#34C759]" />
            </div>
            <p className="text-sm font-bold text-black">{formatCurrency(stats.totalDeposits)}</p>
            <p className="text-[11px] text-[#8E8E93]">總儲值</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/15 flex items-center justify-center mx-auto mb-1">
              <ArrowUpRight className="w-4 h-4 text-[#FF3B30]" />
            </div>
            <p className="text-sm font-bold text-black">{formatCurrency(stats.totalWithdrawals)}</p>
            <p className="text-[11px] text-[#8E8E93]">總提款</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center mx-auto mb-1">
              <TrendingUp className="w-4 h-4 text-[#007AFF]" />
            </div>
            <p className="text-sm font-bold text-black">{formatCurrency(stats.totalVolume)}</p>
            <p className="text-[11px] text-[#8E8E93]">總交易量</p>
          </div>
        </div>
      </Block>

      {/* 7-day volume chart */}
      <BlockTitle className="!mt-4">7 日交易量趨勢</BlockTitle>
      <Block strong inset className="!py-4">
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: '#8E8E93' }}
                axisLine={{ stroke: '#E5E5EA' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#8E8E93' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${(v / 100).toFixed(0)}`}
                width={50}
              />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="volume"
                stroke="#007AFF"
                strokeWidth={2.5}
                dot={{ fill: '#007AFF', r: 4, strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 6, fill: '#007AFF', stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Block>

      {/* Searchbar */}
      <div className="px-4 pt-2 pb-1 bg-[#F2F2F7]">
        <Searchbar
          placeholder="搜尋用戶名稱、交易編號..."
          value={searchQuery}
          onInput={(e: any) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {/* Segmented tabs */}
      <div className="px-4 py-2 bg-[#F2F2F7]">
        <Segmented strong>
          {tabs.map((tab) => (
            <SegmentedButton
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </SegmentedButton>
          ))}
        </Segmented>
      </div>

      {/* Transaction count */}
      <BlockTitle className="!mt-1">
        {filteredTransactions.length} 筆交易
      </BlockTitle>

      {/* Transaction list */}
      {filteredTransactions.length > 0 ? (
        <List strongIos insetIos>
          {filteredTransactions.map((tx) => (
            <ListItem
              key={tx.id}
              link
              chevron={false}
              onClick={() => handleTxTap(tx)}
              media={<TransactionMediaIcon type={tx.type} />}
              title={
                <div className="flex items-center gap-2">
                  <span className="font-medium">{tx.user}</span>
                  <span className="text-[11px] text-[#8E8E93] bg-[#F2F2F7] px-1.5 py-0.5 rounded">
                    {getTypeConfig(tx.type).label}
                  </span>
                </div>
              }
              subtitle={
                <span className="text-[#8E8E93] text-[12px]">
                  {tx.id} &middot; {formatRelativeTime(tx.date)}
                  {tx.status === 'pending' && (
                    <span className="ml-1.5 text-[#FF9500]">
                      <CircleDashed className="w-3 h-3 inline -mt-0.5 mr-0.5" />
                      處理中
                    </span>
                  )}
                </span>
              }
              after={
                <span
                  className="text-[15px] font-semibold tabular-nums"
                  style={{ color: getAmountColor(tx.amount) }}
                >
                  {formatAmount(tx.amount)}
                </span>
              }
            />
          ))}
        </List>
      ) : (
        <Block className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
          <p className="text-[#8E8E93] text-[15px]">
            {searchQuery ? '沒有找到符合的交易' : '沒有交易紀錄'}
          </p>
        </Block>
      )}

      {/* Bottom padding */}
      <div className="h-8" />

      {/* Transaction Detail Sheet */}
      <Sheet
        opened={sheetOpen}
        onBackdropClick={handleCloseSheet}
        className="pb-safe"
      >
        {selectedTx && (
          <div className="flex flex-col">
            {/* Sheet header */}
            <Toolbar top>
              <div className="flex items-center justify-between w-full px-1">
                <span className="text-[17px] font-semibold">
                  交易詳情
                </span>
                <button
                  onClick={handleCloseSheet}
                  className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-[#8E8E93]" />
                </button>
              </div>
            </Toolbar>

            {/* Sheet content */}
            <div className="px-4 pt-2 pb-6">
              {/* Amount highlight */}
              <div className="text-center py-4">
                <div className="mb-2 flex justify-center">
                  <TransactionMediaIcon type={selectedTx.type} />
                </div>
                <p
                  className="text-[32px] font-bold tabular-nums"
                  style={{ color: getAmountColor(selectedTx.amount) }}
                >
                  {formatAmount(selectedTx.amount)}
                </p>
                <p className="text-[14px] text-[#8E8E93] mt-1">
                  {getTypeConfig(selectedTx.type).label}
                </p>
              </div>

              {/* Details */}
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={Hash}
                  label="交易編號"
                  value={selectedTx.id}
                  iconColor="#007AFF"
                />
                <DetailRow
                  icon={User}
                  label="用戶"
                  value={selectedTx.user}
                  iconColor="#AF52DE"
                />
                <DetailRow
                  icon={CreditCard}
                  label="類型"
                  value={getTypeConfig(selectedTx.type).label}
                  iconColor={getTypeConfig(selectedTx.type).color}
                />
                <DetailRow
                  icon={Clock}
                  label="時間"
                  value={selectedTx.date.toLocaleString('zh-TW', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  iconColor="#8E8E93"
                />
                <DetailRow
                  icon={CheckCircle2}
                  label="狀態"
                  value={selectedTx.status === 'completed' ? '已完成' : '處理中'}
                  iconColor={selectedTx.status === 'completed' ? '#34C759' : '#FF9500'}
                  valueColor={selectedTx.status === 'completed' ? '#34C759' : '#FF9500'}
                />
              </div>

              {/* Copy button */}
              <div className="mt-4">
                <Button
                  large
                  outline
                  onClick={() => {
                    navigator.clipboard?.writeText(selectedTx.id)
                    console.log('[Admin] Copied transaction ID')
                  }}
                >
                  <Copy className="w-4 h-4 mr-1.5 inline" />
                  複製交易編號
                </Button>
              </div>
            </div>
          </div>
        )}
      </Sheet>
    </>
  )
}
